import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Home: React.FC = () => {
  const featuredServices = [
    {
      title: 'Guided Fishing Tours',
      description: 'Expert-led fishing expeditions for all skill levels',
      icon: '🎣'
    },
    {
      title: 'Equipment Rental',
      description: 'High-quality fishing gear available for rent',
      icon: '🎒'
    },
    {
      title: 'Fishing Lessons',
      description: 'Learn fishing techniques from professional anglers',
      icon: '📚'
    }
  ];

  const testimonials = [
    {
      name: 'John Smith',
      role: 'Amateur Angler',
      content: 'An incredible experience! The guides were knowledgeable and made sure we had a great time.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      role: 'Professional Fisher',
      content: 'The equipment provided was top-notch, and the locations were perfect for fishing.',
      rating: 5
    },
    {
      name: 'Michael Brown',
      role: 'First-time Fisher',
      content: 'As a beginner, I was nervous, but the team made me feel comfortable and taught me so much.',
      rating: 4
    }
  ];

  const blogPosts = [
    {
      title: 'Best Fishing Spots in the UK',
      date: '2024-02-15',
      excerpt: 'Discover the top fishing locations across the United Kingdom...',
      author: 'David Wilson',
      image: '/images/fishing2.jpg'
    },
    {
      title: 'Essential Fishing Gear Guide',
      date: '2024-02-10',
      excerpt: 'A comprehensive guide to the must-have equipment for every angler...',
      author: 'Emma Thompson',
      image: '/images/fishing3.jpg'
    },
    {
      title: 'Fishing Techniques for Beginners',
      date: '2024-02-05',
      excerpt: 'Learn the fundamental techniques to get started with fishing...',
      author: 'James Anderson',
      image: '/images/fishing5.jpg'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Anchor Management - Premier Fishing Services at Loch Lomond</title>
        <meta name="description" content="Experience the best fishing adventures at Loch Lomond with Anchor Management. Expert guided tours, equipment rental, and unforgettable fishing experiences in Scotland's most beautiful location." />
        <meta name="keywords" content="fishing, Loch Lomond, Scotland, guided tours, equipment rental, fishing lessons" />
        <meta property="og:title" content="Anchor Management - Premier Fishing Services at Loch Lomond" />
        <meta property="og:description" content="Experience the best fishing adventures at Loch Lomond with Anchor Management. Expert guided tours, equipment rental, and unforgettable fishing experiences." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://anchormanagement.co.uk/" />
        <meta property="og:image" content="/images/scenery 1.jpg" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative min-h-[180vh] md:min-h-[180vh] lg:min-h-[300vh] flex items-center justify-center overflow-hidden"
      >
        {/* Background image and overlay */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="/images/keen3.jpg"
            alt="Loch Lomond fishing scene"
            className="w-full h-full object-cover object-center lg:object-[center_80%] md:scale-105 transition-transform duration-700 ease-in-out animate-float"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80"></div>
        </div>
        {/* Hero content: vertically centred using flex */}
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto md:pt-16 lg:pt-32">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="text-6xl md:text-8xl font-bold mb-10 text-shadow-lg"
          >
            Welcome to Anchor Management
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            className="text-2xl md:text-4xl mb-16 max-w-4xl mx-auto text-shadow-md"
          >
            Experience the thrill of fishing at Scotland's most beautiful loch
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: 'easeOut' }}
            className="flex flex-col sm:flex-row justify-center gap-8"
          >
            <Link 
              to="/contact" 
              className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-10 py-5 rounded-full font-semibold hover:from-primary-700 hover:to-primary-600 transition-all transform hover:scale-105 shadow-lg text-xl relative overflow-hidden group"
            >
              <span className="relative z-10">Book Now</span>
              <div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-primary-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
            </Link>
            <Link 
              to="/about" 
              className="border-2 border-white text-white px-10 py-5 rounded-full font-semibold hover:bg-white hover:text-primary-600 transition-all transform hover:scale-105 text-xl relative overflow-hidden group"
            >
              <span className="relative z-10">Learn More</span>
              <div className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
            </Link>
          </motion.div>
        </div>
        {/* Wave Shape at the bottom */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            className="w-full h-32 md:h-40"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#00B4D8"
              fillOpacity="0.9"
              d="M0,160L48,170.7C96,181,192,203,288,208C384,213,480,203,576,181.3C672,160,768,128,864,128C960,128,1056,160,1152,170.7C1248,181,1344,171,1392,165.3L1440,160L1440,320L0,320Z"
            />
          </svg>
        </div>
      </section>

      {/* Loch Lomond Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/scenery2.jpg"
                  alt="Loch Lomond scenic view"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-4xl font-bold mb-6 text-gray-800 dark:text-white">
                Discover Loch Lomond
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                Nestled in the heart of the Trossachs National Park, Loch Lomond is Scotland's largest freshwater loch and one of its most stunning natural wonders. Stretching 24 miles long and up to 5 miles wide, this magnificent body of water is surrounded by breathtaking mountains and lush forests.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
                Known for its crystal-clear waters and abundant wildlife, Loch Lomond offers some of the finest fishing opportunities in Scotland. The loch is home to a variety of fish species, including brown trout, pike, and the famous Loch Lomond salmon, making it a paradise for anglers of all skill levels.
              </p>
              <Link
                to="/about"
                className="inline-block bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-4 rounded-full font-semibold hover:from-primary-700 hover:to-primary-600 transition-all transform hover:scale-105 shadow-lg"
              >
                Learn More About Our Location
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800 dark:text-white">Featured Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl p-8 text-center hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="text-5xl mb-6 bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">{service.icon}</div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">{service.title}</h3>
                <p className="text-gray-700 dark:text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800 dark:text-white">What Our Customers Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl p-8 hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="flex items-center mb-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white">{testimonial.name}</h3>
                    <p className="text-gray-700 dark:text-gray-300">{testimonial.role}</p>
                  </div>
                  <div className="text-yellow-500 text-xl">
                    {'★'.repeat(testimonial.rating)}
                    {'☆'.repeat(5 - testimonial.rating)}
                  </div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 italic">"{testimonial.content}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800 dark:text-white">Latest from Our Blog</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="relative h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">{post.title}</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">{post.excerpt}</p>
                  <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
                    <span>{post.author}</span>
                    <time>{new Date(post.date).toLocaleDateString('en-GB')}</time>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800 dark:text-white">Our Locations</h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative h-[400px] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2234.1234567890123!2d-4.6333!3d56.0833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTbCsDA1JzAwLjAiTiA0wrAzOCcwMC4wIlc!5e0!3m2!1sen!2suk!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Loch Lomond Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
